/* eslint-disable react/jsx-curly-newline */
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// React Qr Code
import QRCode from 'react-qr-code';

// Material
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';

// Helpers
import { currencyFormat } from 'helpers/currencyFormat';
import { getComparator, stableSort } from 'helpers/tableSortLabels';
import {
  formatDateString,
  getHoursFromToIoSoDate,
  getMinutesFromToIoSoDate,
  prettieDateAndTime,
} from 'helpers';
import {
  getStorageLineList,
  getStorageUserClaims,
  setStorageLineList,
} from 'helpers/ticketSales';
import { formatDocument } from 'helpers/document';

// Actions
import {
  cancelBookedTokenRequest,
  userTransactionListRequest,
} from 'store/ducks/itineraries/actions';
import { appUserSendUserTransactionViaEmailRequest } from 'store/ducks/users/actions';
import {
  setReschduleData,
  setStep,
  setTicketDataRequest,
} from 'store/ducks/publicStore/actions';
import { setSecondOpen } from 'store/ducks/dialogs/actions';
import { listLinesRequest } from 'store/ducks/lines/actions';

// Types
import { ApplicationState } from 'store';
import {
  TripsOfResumeItineraryVesselsInterface,
  UserTrasactionInterface,
} from 'store/ducks/itineraries/types';

// Icons
import { Cancel, Copy, Watch } from 'icons/Tools_Interface';
import { Qrcode as QrcodeIcon } from 'icons/Shopping_Payment_Finance';

// Constants
import FARE_CONST from 'constants/fares';
import {
  PAYMENT_TYPE,
  STATUS_PRIORITY,
  PaymentStatusInterface,
} from 'constants/ticketSales';
import PATH from 'constants/basePath';
import ENVIRONMENTS from 'constants/enviroments';
import { SEAT_TYPE } from 'pages/public/TicketSales/pages/TicketDetails/components/SelectTrip';

// Icons
import { Arrow2Down, Arrow2Up } from 'icons/Arrows';
import { Bus, FerryFill } from 'icons/Location_Transport';
import { ToSend } from 'icons/Comunication';

// Components
import ErrorBoundary from 'components/ErrorBoundary';
import Qrcode from './Qrcode';

// Styles
import useStyles from './styles';

export interface ItineraryListInterface
  extends TripsOfResumeItineraryVesselsInterface {
  footCapacity: string;
  vehicleCapacity: string;
}

interface TokenListInterface {
  userId: string;
  token: boolean;
  hideUserData?: boolean;
}

const TokenList: React.FC<TokenListInterface> = ({
  userId,
  token,
  hideUserData = false,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const userClaims = getStorageUserClaims();
  const storegeLineList = JSON.parse(getStorageLineList());
  const IS_SHIP = ENVIRONMENTS.TICKET_SALES.ITINERARY_TYPE === 'ship';

  const [tockenCollapse, setTockenCollapse] = useState<{
    [key: string]: boolean;
  }>({});
  const [deleteDialog, setDeleteDialog] = useState(false);

  const tokenList = useSelector(
    (state: ApplicationState) => state.itineraries.userTransactionList,
  );
  const ticketData = useSelector(
    (state: ApplicationState) => state.publicStore.ticketData,
  );
  const step = useSelector((state: ApplicationState) => state.publicStore.step);
  const linesList = useSelector(
    (state: ApplicationState) => state.lines.linesList || [],
  );

  const handleCopy = () => {
    const copyText = document.getElementById('inputToCopy') as HTMLInputElement;
    if (copyText) {
      // do not delete variables names
      const unusualeValue1 = copyText?.select();
      const unusualeValue2 = copyText?.setSelectionRange(0, 99999);
      navigator.clipboard.writeText(copyText.value);
    }
  };

  /**
   * Limite de reagendamentos por bilhete, vindo de
   * REACT_APP_TICKET_SALES_RESCHEDULE_MAX. Valor ausente ou inválido vira 1.
   */
  const maxReschedules = (() => {
    const raw = ENVIRONMENTS.TICKET_SALES.RESCHEDULE_MAX;
    const parsed = Number(raw);
    return raw && raw.trim() !== '' && Number.isInteger(parsed) && parsed >= 0
      ? parsed
      : 1;
  })();

  /**
   * Antecedência mínima (em horas) da partida para permitir reagendar, vinda de
   * REACT_APP_TICKET_SALES_RESCHEDULE_MIN_HOURS. Valor ausente ou inválido vira 0
   * (sem restrição de horário).
   */
  const minHoursToReschedule = (() => {
    const raw = ENVIRONMENTS.TICKET_SALES.RESCHEDULE_MIN_HOURS;
    const parsed = Number(raw);
    return raw && raw.trim() !== '' && Number.isFinite(parsed) && parsed >= 0
      ? parsed
      : 0;
  })();

  const isAvalibleToReschedule = (
    createdAt: string,
    expiresAt: string,
    start: string,
  ) => {
    if (createdAt && expiresAt) {
      const strDate = expiresAt.split('T')[0];
      const year = Number(strDate.split('-')[0]);
      const month = Number(strDate.split('-')[1]);
      const yearToday = new Date().getFullYear();
      const monthToday = new Date().getMonth() + 1;
      if (yearToday > year) return false;
      if (yearToday === year && monthToday > month) return false;
      if (minHoursToReschedule === 0) return true;

      const expiresAtStr = expiresAt.split('T')[0].split('-');
      const expiresAtYear = Number(expiresAtStr[0]);
      const expiresAtMonth = Number(expiresAtStr[1]);
      const expiresAtDay = Number(expiresAtStr[2]);

      const startStr = start.split('T')[1].split(':');
      const startHour = Number(startStr[0]);
      const startMinutes = Number(startStr[1]);

      const result =
        new Date(
          new Date(expiresAtYear, expiresAtMonth - 1, expiresAtDay).setHours(
            startHour,
            startMinutes,
          ),
        ).getTime() - new Date().getTime();
      const hours = result / (1000 * 60 * 60);

      return hours > minHoursToReschedule;
    }
    return false;
  };

  const findLastAmount = (rescheduleTokenId: string) => {
    const found = tokenList.find((transaction) =>
      transaction.Tokens.find(
        (tokenItem) => tokenItem.id === rescheduleTokenId,
      ),
    );
    let amunt = 0;
    if (found) {
      // to do: verificar por que o back está retornando valor 0 no reagendamento
      amunt =
        found.Tokens.find((tokenItem) => tokenItem.id === rescheduleTokenId)
          ?.UserTransactionToken.amount || 0;
    }
    return amunt;
  };

  useEffect(() => {
    if (token) {
      if (storegeLineList.length === 0) {
        dispatch(
          listLinesRequest({
            loading: true,
            body: {
              transportOperatorId: ENVIRONMENTS.OPERATOR_ID,
              _limit: 9999,
            },
          }),
        );
      }
      dispatch(
        userTransactionListRequest({
          body: {
            _limit: 9999,
            userId,
            authUserId: userId,
            _includes: 'token,paymentrequest,section',
          },
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (linesList.length > 0) {
      setStorageLineList(JSON.stringify(linesList));
    }
  }, [linesList]);

  useEffect(() => {
    if (tokenList.length > 0) {
      const aux: { [key: string]: boolean } = {};
      tokenList.forEach((token) => {
        aux[token?.id] = false;
      });
      setTockenCollapse(aux);
    }
  }, []);

  return (
    <ErrorBoundary>
      <>
        {Array.isArray(tokenList) &&
          tokenList.length > 0 &&
          stableSort(tokenList, getComparator('desc', 'createdAt')).map(
            (trasaction: UserTrasactionInterface) => {
              const STATUS_CONFIRMED = 2;
              const TOKEN_STATUS_CONFIRMED = 0;
              const TOKEN_STATUS_CANCELED = 3;
              let confirmed = false;
              let canceled = false;
              let processing = false;
              let paymentStatus = '---';
              const statusKey = trasaction?.PaymentRequest?.status;
              let tokenStatusKey = trasaction?.Tokens[0]?.status;
              const userTransactionsTpye = trasaction?.type;
              const paymentTypy = PAYMENT_TYPE as PaymentStatusInterface;

              if (statusKey === STATUS_CONFIRMED) {
                let aux: number | null = null;
                STATUS_PRIORITY.forEach(({ KEY }) => {
                  const found = trasaction.Tokens.find(
                    (item) => item.status === KEY,
                  );
                  if (found && aux === null) aux = found.status;
                });
                if (aux !== null) tokenStatusKey = aux;
                if (aux === TOKEN_STATUS_CANCELED) canceled = true;
              }

              if (
                paymentTypy?.[statusKey]?.VALUE ||
                (paymentTypy?.[userTransactionsTpye]?.VALUE && !canceled)
              ) {
                paymentStatus =
                  paymentTypy?.[statusKey]?.VALUE ||
                  paymentTypy?.[userTransactionsTpye]?.VALUE;
                if (paymentTypy?.[userTransactionsTpye]?.VALUE) {
                  confirmed = true;
                }
                processing = !!paymentTypy?.[statusKey]?.PROCESSING;
              } else {
                paymentStatus =
                  paymentTypy?.[statusKey]?.[tokenStatusKey]?.VALUE || '---';
                confirmed = !!paymentTypy?.[statusKey]?.[tokenStatusKey]
                  ?.CONFIRMED;
              }

              const found = trasaction?.PaymentRequest?.PaymentRequestHistories.find(
                (item: {
                  message: string;
                  toStatus: number;
                  fromStatus: number;
                }) => item?.fromStatus === 0 && item?.toStatus === 1,
              );

              let qrCode = '';
              if (processing && found) {
                qrCode = JSON.parse(found.message)?.qrcode;
              }

              return (
                <Box className={classes.order} m='1rem 0'>
                  <Grid
                    className={classes.orderContent}
                    container
                    xl={12}
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}>
                    <Grid xl={6} lg={6} md={6} sm={6} xs={12}>
                      <Typography
                        className={classes.item}
                        style={{ fontSize: '1rem', margin: '0.25rem 0' }}>
                        Pedido {trasaction?.id?.split('-')[0]}
                      </Typography>
                      <Typography
                        className={classes.item}
                        style={{ margin: '0.25rem 0' }}>
                        {currencyFormat({
                          value: trasaction?.amount || 0,
                        })}
                      </Typography>
                    </Grid>
                    <Grid
                      container
                      justifyContent='space-between'
                      alignItems='center'
                      xl={6}
                      lg={6}
                      md={6}
                      sm={6}
                      xs={12}>
                      <Typography className={classes.item}>
                        {prettieDateAndTime(trasaction?.createdAt || '')}
                      </Typography>
                      <Box display='flex' alignItems='center'>
                        <Typography
                          className={classes.item}
                          style={{
                            color: confirmed ? '#22D76A' : '#000',
                            marginRight: '0.5rem',
                          }}>
                          {paymentStatus}
                        </Typography>
                        <IconButton
                          aria-controls='menu-appbar'
                          aria-haspopup='true'
                          aria-label='account of current user'
                          onClick={() =>
                            setTockenCollapse((s) => ({
                              ...s,
                              [trasaction?.id]: !s[trasaction?.id],
                            }))
                          }>
                          {tockenCollapse[trasaction?.id] ? (
                            <Arrow2Up
                              height='0.875rem'
                              width='0.875rem'
                              fill='#A3BBCC'
                            />
                          ) : (
                            <Arrow2Down
                              height='0.875rem'
                              width='0.875rem'
                              fill='#A3BBCC'
                            />
                          )}
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                  {tockenCollapse[trasaction?.id] &&
                    trasaction.Tokens.filter(
                      (obj) => obj.status === TOKEN_STATUS_CONFIRMED,
                    ).map((item, i) => {
                      const reschedule = item.UserTransactionToken?.reschedules;
                      let car: {
                        type: string;
                        brand: string;
                        model: string;
                        licensePlate: string;
                        passengers: { profileId: string }[];
                      } | null = null;

                      if (item.vehicleData) {
                        car = JSON.parse(item.vehicleData!);
                      }
                      const boardingData = item?.UserTransactionTokens.find(
                        (e) => e.id === item.UserTransactionToken.id,
                      );
                      return (
                        <Box p='0 1rem'>
                          <Box width='100%'>
                            <Divider variant='fullWidth' />
                            <Box pt='1rem'>
                              <Typography variant='h6' className={classes.item}>
                                Dados da Viagem
                              </Typography>
                              <Typography className={classes.item}>
                                Linha:{' '}
                                {storegeLineList.find(
                                  (e: { id: string; name: string }) =>
                                    e.id ===
                                    item.TripSchedule?.ScheduleMatrixConfig
                                      ?.LineSection?.lineId,
                                )?.name || '---'}
                              </Typography>
                              <Typography className={classes.item}>
                                Trecho:{' '}
                                {item.TripSchedule?.ScheduleMatrixConfig
                                  ?.LineSection?.name || '---'}
                              </Typography>
                              {item?.TripScheduleSeat && (
                                <Typography className={classes.item}>
                                  Piso: {item?.TripScheduleSeat?.floor || '---'}
                                </Typography>
                              )}
                              <Box className={classes.seatType}>
                                {item?.TripScheduleSeat && (
                                  <Typography
                                    className={classes.item}
                                    style={{ marginRight: '0.5rem' }}>
                                    Poltrona{' '}
                                    {item?.TripScheduleSeat?.seatNumber ||
                                      '---'}
                                  </Typography>
                                )}
                                <Box p='0.5rem'>
                                  {
                                    SEAT_TYPE?.[
                                      item?.TripScheduleSeat
                                        ?.seatType as keyof typeof SEAT_TYPE
                                    ]?.ICON
                                  }
                                </Box>
                                <Typography>
                                  {
                                    SEAT_TYPE?.[
                                      item?.TripScheduleSeat
                                        ?.seatType as keyof typeof SEAT_TYPE
                                    ]?.LABEL
                                  }
                                </Typography>
                              </Box>
                              <Box
                                display='flex'
                                alignItems='center'
                                pt='0.5rem'>
                                <Typography
                                  className={classes.item}
                                  style={{ marginRight: '0.25rem' }}>
                                  Partida:
                                </Typography>
                                <Typography
                                  className={classes.item}
                                  style={{ marginRight: '1rem' }}>
                                  {formatDateString(
                                    item.TripSchedule?.date || '',
                                  )}
                                </Typography>
                                <Box display='flex' alignItems='center'>
                                  {IS_SHIP && (
                                    <Typography className={classes.item}>
                                      {getHoursFromToIoSoDate(
                                        item.TripSchedule?.open || '',
                                      )}
                                      :
                                      {getMinutesFromToIoSoDate(
                                        item.TripSchedule?.open || '',
                                      )}
                                    </Typography>
                                  )}
                                  <Box p='0.5rem'>
                                    {IS_SHIP ? (
                                      <FerryFill
                                        fill='#0094FF'
                                        width='1.25rem'
                                        height='1.25rem'
                                      />
                                    ) : (
                                      <Bus
                                        fill='#0094FF'
                                        width='1.25rem'
                                        height='1.25rem'
                                      />
                                    )}
                                  </Box>
                                  <Typography className={classes.item}>
                                    {getHoursFromToIoSoDate(
                                      item.TripSchedule?.start || '',
                                    )}
                                    :
                                    {getMinutesFromToIoSoDate(
                                      item.TripSchedule?.start || '',
                                    )}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                            {!hideUserData && (
                              <Box pt='1rem'>
                                <Typography
                                  variant='h6'
                                  className={classes.item}>
                                  Dados do Usuário
                                </Typography>
                                <Typography className={classes.item}>
                                  Nome:{' '}
                                  {JSON.parse(userClaims)?.claims?.name ||
                                    '---'}
                                </Typography>
                                <Typography className={classes.item}>
                                  Documento:{' '}
                                  {JSON.parse(userClaims)?.document
                                    ? formatDocument(
                                        0,
                                        JSON.parse(userClaims)?.document,
                                      )
                                    : '---'}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Box>
                            {car && (
                              <>
                                <Typography className={classes.subItem}>
                                  {car?.model} (
                                  {FARE_CONST.VEHICLE_TYPES.find(
                                    ({ KEY }) => KEY === car?.type,
                                  )?.VALUE ||
                                    'Tipo do Automoveo não encontrado'}
                                  )
                                </Typography>

                                <Typography className={classes.subItem}>
                                  {car?.passengers.length} Passageiro Comum
                                </Typography>
                              </>
                            )}
                            <Box visibility='hidden' maxHeight='0.5rem'>
                              <input
                                type='text'
                                id='inputToCopy'
                                value={qrCode}
                              />
                            </Box>
                            {processing && i === 0 && qrCode && (
                              <>
                                <Box className={classes.contentImage}>
                                  <QRCode value={qrCode || ''} />
                                </Box>
                                <Button
                                  className={classes.copyCodeButton}
                                  onClick={() => handleCopy()}>
                                  <Copy
                                    width='1rem'
                                    height='1rem'
                                    fill='#fff'
                                  />
                                  <Typography
                                    style={{
                                      fontSize: '0.9rem',
                                      paddingLeft: '0.5rem',
                                      whiteSpace: 'nowrap',
                                      textOverflow: 'ellipsis',
                                      overflow: 'hidden',
                                    }}>
                                    Copiar código
                                  </Typography>
                                </Button>
                              </>
                            )}
                          </Box>

                          <Box width='100%'>
                            <Divider variant='fullWidth' />
                            {confirmed && (
                              <Grid
                                container
                                xl={12}
                                lg={12}
                                md={12}
                                sm={12}
                                xs={12}
                                style={{
                                  display: 'flex',
                                  width: '100%',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '0.5rem 0',
                                }}>
                                <Grid xl={9} lg={9} md={9} sm={8} xs={12}>
                                  <Grid
                                    xl={12}
                                    lg={12}
                                    md={12}
                                    sm={12}
                                    xs={12}
                                    container
                                    alignItems='center'>
                                    <Grid xl={3} lg={3} md={3} sm={12} xs={12}>
                                      <Button
                                        variant='text'
                                        style={{
                                          textTransform: 'none',
                                          borderRadius: '0.75rem',
                                          color: '#0094FF',
                                        }}
                                        onClick={() =>
                                          dispatch(
                                            appUserSendUserTransactionViaEmailRequest(
                                              {
                                                userTransactionId:
                                                  trasaction?.id,
                                              },
                                            ),
                                          )
                                        }>
                                        <Box display='flex' alignItems='center'>
                                          <ToSend fill='#0094FF' />
                                          <Typography
                                            style={{
                                              marginLeft: '0.5rem',
                                            }}>
                                            Enviar por e-mail
                                          </Typography>
                                        </Box>
                                      </Button>
                                    </Grid>

                                    {isAvalibleToReschedule(
                                      item.createdAt,
                                      item.expiresAt,
                                      item.TripSchedule?.start,
                                    ) &&
                                      (reschedule || 0) < maxReschedules && (
                                        <Grid
                                          xl={3}
                                          lg={3}
                                          md={3}
                                          sm={12}
                                          xs={12}>
                                          <Button
                                            variant='text'
                                            style={{
                                              textTransform: 'none',
                                              borderRadius: '0.75rem',
                                              color: '#0094FF',
                                            }}
                                            onClick={() => {
                                              let {
                                                amount,
                                              } = item.UserTransactionToken;
                                              if (reschedule === 1) {
                                                amount = findLastAmount(
                                                  trasaction?.rescheduledTokenId,
                                                );
                                              }

                                              dispatch(
                                                setReschduleData({
                                                  vehicleType: car?.type || '',
                                                  amount,
                                                  reschedule,
                                                  tokenId: item.id,
                                                  profileId: item.profileId,
                                                  passengers:
                                                    car?.passengers.length || 0,
                                                  startStopId:
                                                    item.TripSchedule
                                                      .ScheduleMatrixConfig
                                                      .LineSection.Section
                                                      .startId,
                                                  endStopId:
                                                    item.TripSchedule
                                                      .ScheduleMatrixConfig
                                                      .LineSection.Section
                                                      .endId,
                                                }),
                                              );
                                              dispatch(
                                                setTicketDataRequest({
                                                  ...ticketData,
                                                  date: item.TripSchedule.date,
                                                  selectedDay:
                                                    item.TripSchedule.date,
                                                }),
                                              );
                                              dispatch(
                                                setStep({
                                                  go: {
                                                    ...step.go,
                                                    selected: true,
                                                  },
                                                  back: {
                                                    ...step.back,
                                                    toShow: false,
                                                  },
                                                  details: {
                                                    ...step.details,
                                                    selected: false,
                                                    toShow: false,
                                                  },
                                                  confirmRescheduling: {
                                                    ...step.confirmRescheduling,
                                                    toShow: true,
                                                  },
                                                  pay: {
                                                    ...step.pay,
                                                    selected: false,
                                                    toShow: reschedule === 1,
                                                  },
                                                }),
                                              );
                                              history.push(
                                                PATH.TICKET_RESERVATION_WEBSITE
                                                  .TICKET_DETAILS,
                                              );
                                            }}>
                                            <Box
                                              display='flex'
                                              alignItems='center'>
                                              <Watch fill='#0094FF' />
                                              <Typography
                                                style={{
                                                  marginLeft: '0.5rem',
                                                }}>
                                                Reagendar
                                              </Typography>
                                            </Box>
                                          </Button>
                                        </Grid>
                                      )}
                                    <Grid xl={3} lg={3} md={3} sm={12} xs={12}>
                                      <Button
                                        variant='text'
                                        style={{
                                          textTransform: 'none',
                                          borderRadius: '0.75rem',
                                          color: '#0094FF',
                                        }}
                                        onClick={() => {
                                          setDeleteDialog(true);
                                        }}>
                                        <Box display='flex' alignItems='center'>
                                          <Cancel fill='#0094FF' />
                                          <Typography
                                            style={{
                                              marginLeft: '0.5rem',
                                            }}>
                                            Cancelar
                                          </Typography>
                                        </Box>
                                      </Button>
                                      <Dialog
                                        open={deleteDialog}
                                        onClose={() => setDeleteDialog(false)}
                                        maxWidth='xs'
                                        fullWidth>
                                        <DialogTitle disableTypography>
                                          <Typography
                                            style={{
                                              fontWeight: 600,
                                              fontSize: 16,
                                            }}>
                                            Excluir passagem
                                          </Typography>
                                        </DialogTitle>
                                        <DialogContent>
                                          <Typography style={{ fontSize: 14 }}>
                                            Tem certeza que deseja excluir a
                                            passagem{' '}
                                            <strong>
                                              {trasaction?.id?.split('-')[0]}
                                            </strong>
                                          </Typography>
                                        </DialogContent>
                                        <DialogActions
                                          style={{
                                            padding: '8px 24px 16px',
                                          }}>
                                          <Button
                                            onClick={() =>
                                              setDeleteDialog(false)
                                            }
                                            style={{
                                              color: '#657786',
                                            }}>
                                            Cancelar
                                          </Button>
                                          <Button
                                            variant='contained'
                                            onClick={() => {
                                              dispatch(
                                                cancelBookedTokenRequest({
                                                  body: {
                                                    tokenId: item?.id || '',
                                                    userId,
                                                  },
                                                }),
                                              );
                                              setDeleteDialog(false);
                                            }}
                                            style={{
                                              borderRadius: 8,
                                              minWidth: 80,
                                              backgroundColor: '#0094FF',
                                              color: '#fff',
                                            }}>
                                            Excluir
                                          </Button>
                                        </DialogActions>
                                      </Dialog>
                                    </Grid>
                                    {item?.token && (
                                      <Grid
                                        xl={3}
                                        lg={3}
                                        md={3}
                                        sm={12}
                                        xs={12}>
                                        <Button
                                          variant='text'
                                          style={{
                                            textTransform: 'none',
                                            borderRadius: '0.75rem',
                                            color: '#0094FF',
                                          }}
                                          onClick={() => {
                                            dispatch(
                                              setSecondOpen({
                                                open: true,
                                                wasCalled: true,
                                                disableBackdropClick: true,
                                                content: (
                                                  <Qrcode
                                                    qrCode={item?.token}
                                                    item={item}
                                                    hideUserData={hideUserData}
                                                  />
                                                ),
                                                maxWidth: 'lg',
                                              }),
                                            );
                                          }}>
                                          <Box
                                            display='flex'
                                            alignItems='center'>
                                            <QrcodeIcon fill='#0094FF' />
                                            <Typography
                                              style={{
                                                marginLeft: '0.5rem',
                                              }}>
                                              QRCode da Passagem
                                            </Typography>
                                          </Box>
                                        </Button>
                                      </Grid>
                                    )}
                                    {boardingData?.FareTaxIntegratorStock
                                      ?.number && (
                                      <Grid
                                        xl={3}
                                        lg={3}
                                        md={3}
                                        sm={12}
                                        xs={12}>
                                        <Button
                                          variant='text'
                                          style={{
                                            textTransform: 'none',
                                            borderRadius: '0.75rem',
                                            color: '#0094FF',
                                          }}
                                          onClick={() => {
                                            dispatch(
                                              setSecondOpen({
                                                open: true,
                                                wasCalled: true,
                                                disableBackdropClick: true,
                                                content: (
                                                  <Qrcode
                                                    item={item}
                                                    hideUserData={hideUserData}
                                                    qrCode={
                                                      boardingData
                                                        ?.FareTaxIntegratorStock
                                                        ?.number
                                                    }
                                                  />
                                                ),
                                                maxWidth: 'lg',
                                              }),
                                            );
                                          }}>
                                          <Box
                                            display='flex'
                                            alignItems='center'>
                                            <QrcodeIcon fill='#0094FF' />
                                            <Typography
                                              style={{
                                                marginLeft: '0.5rem',
                                              }}>
                                              QRCode Terminal
                                            </Typography>
                                          </Box>
                                        </Button>
                                      </Grid>
                                    )}
                                  </Grid>
                                </Grid>
                                <Grid xl={3} lg={3} md={3} sm={4} xs={12}>
                                  <Typography
                                    style={{
                                      fontSize: '0.9rem',
                                      paddingLeft: '0.5rem',
                                      whiteSpace: 'nowrap',
                                      textOverflow: 'ellipsis',
                                      overflow: 'hidden',
                                    }}>
                                    Passagem {item?.id?.split('-')[0]}
                                  </Typography>
                                  {/* <Button
                              variant='text'
                              style={{
                                textTransform: 'none',
                                color: '#E22020',
                                borderRadius: '0.75rem',
                              }}>
                              Cancelar pedido
                            </Button> */}
                                </Grid>
                              </Grid>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                </Box>
              );
            },
          )}
      </>
    </ErrorBoundary>
  );
};

export { TokenList };
export default TokenList;
